import * as Collapsible from "@/components/ui/collapsible";
import { A } from "@solidjs/router";
import { Check, ChevronDown, ListCheck, Play, ScrollText } from "lucide-solid";
import { For, Index, Show, type Component } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { useCourseOverview } from "../useCourseOverview";
import Toolbar from "./Toolbar";

type Section = ReturnType<ReturnType<typeof useCourseOverview>['sectionsWithState']>[number]
type Lesson = Section['lessons'][number]

const typeIcon = (type: Lesson['type'], done: boolean): Component<{ size?: number }> => {
  if (done) return Check
  if (type === 'video') return Play
  if (type === 'quiz') return ListCheck
  return ScrollText
}

export default function () {
  const { visibleSections, isSectionOpen, setSectionOpen } = useService(useCourseOverview)()

  return (
    <>
      <Toolbar />

      <Grid gap="9px">
        <Index
          each={visibleSections()}
          fallback={
            <Box textAlign="center" color="editorialInk3" fontSize="13px" py="22px">
              No sections in this view.
            </Box>
          }
        >
          {(section, index) => (
            <SectionRow
              section={section()}
              index={index}
              open={isSectionOpen(section().id)}
              onOpenChange={(open) => setSectionOpen(section().id, open)}
            />
          )}
        </Index>
      </Grid>
    </>
  )
}

function SectionRow(props: { section: Section; index: number; open: boolean; onOpenChange: (open: boolean) => void }) {
  const pct = () => (props.section.total ? Math.round((props.section.done / props.section.total) * 100) : 0)

  return (
    <Box bg="editorialPanel" border="1px solid var(--colors-editorial-hair)" borderRadius="12px" overflow="hidden">
      <Collapsible.Root open={props.open} onOpenChange={(e) => props.onOpenChange(e.open)}>
        <Collapsible.Trigger
          asChild={(triggerProps) => (
            <Grid
              {...triggerProps()}
              gridTemplateColumns={{ base: '34px 1fr auto 20px', smDown: '30px 1fr 20px' }}
              columnGap="13px"
              alignItems="center"
              width="100%"
              p="12px 15px"
              bg="transparent"
              border="none"
              fontFamily="inherit"
              cursor="pointer"
              textAlign="left"
              class={css({ '&:hover': { bg: 'editorialHover' } })}
            >
              <Flex
                width="32px"
                height="32px"
                borderRadius="999px"
                align="center"
                justify="center"
                flex="none"
                fontSize="12px"
                fontWeight="700"
                border="1px solid"
                borderColor={props.section.state === 'active' ? 'editorialAccent' : 'var(--colors-editorial-hair-strong)'}
                bg={props.section.state === 'done' ? 'editorialAccent' : 'transparent'}
                color={
                  props.section.state === 'done'
                    ? 'editorialBadgeFg'
                    : props.section.state === 'active'
                    ? 'editorialAccentDeep'
                    : 'editorialInk3'
                }
              >
                <Show when={props.section.state === 'done'} fallback={String(props.index + 1).padStart(2, '0')}>
                  <Check size={16} />
                </Show>
              </Flex>

              <Box minWidth="0">
                <Box
                  fontSize="14px"
                  fontWeight={props.section.state === 'done' ? '600' : '700'}
                  color={props.section.state === 'done' ? 'editorialInk2' : 'editorialInk'}
                  letterSpacing="-.01em"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {props.section.title}
                </Box>
                <Box fontSize="11.5px" color="editorialInk3" mt="2px">
                  {props.section.topics} topics{props.section.quizzes ? ` · ${props.section.quizzes} quiz` : ''}
                </Box>
              </Box>

              <Flex align="center" gap="9px" display={{ base: 'flex', smDown: 'none' }}>
                <Box
                  width="96px"
                  height="5px"
                  borderRadius="999px"
                  background="color-mix(in srgb, var(--colors-editorial-ink) 11%, transparent)"
                  overflow="hidden"
                >
                  <Box height="100%" borderRadius="999px" bg="editorialAccent" style={{ width: `${pct()}%` }} />
                </Box>
                <Box fontSize="11.5px" color="editorialInk3" minWidth="32px" textAlign="right">
                  {props.section.done}/{props.section.total}
                </Box>
              </Flex>

              <Box
                color="editorialInk3"
                transition="transform .2s"
                style={{ transform: props.open ? 'rotate(180deg)' : 'none' }}
              >
                <ChevronDown size={20} />
              </Box>
            </Grid>
          )}
        />

        <Collapsible.Content>
          <Grid gap="2px" p="4px 12px 10px" borderTop="1px solid var(--colors-editorial-hair)">
            <For each={props.section.lessons}>
              {(lesson) => <TopicRow lesson={lesson} />}
            </For>
          </Grid>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  )
}

function TopicRow(props: { lesson: Lesson }) {
  const clickable = () => props.lesson.done || props.lesson.current;

  const iconBg = () => {
    if (props.lesson.current) return 'color-mix(in srgb, var(--colors-editorial-accent) 18%, transparent)'
    if (props.lesson.done) return 'color-mix(in srgb, var(--colors-editorial-accent) 14%, transparent)'
    return 'editorialHover'
  }

  const row = (
    <Grid
      gridTemplateColumns="28px 1fr"
      gap="11px"
      alignItems="center"
      p="9px 8px"
      borderRadius="8px"
      cursor={clickable() ? 'pointer' : 'default'}
      background={props.lesson.current ? 'color-mix(in srgb, var(--colors-editorial-accent) 10%, transparent)' : 'transparent'}
      class={css({
        boxShadow: props.lesson.current ? 'inset 2px 0 0 var(--colors-editorial-accent)' : 'none',
        '&:hover': clickable() && !props.lesson.current ? { bg: 'editorialHover' } : {},
      })}
    >
      <Flex
        width="28px"
        height="28px"
        borderRadius="7px"
        align="center"
        justify="center"
        flex="none"
        background={iconBg()}
        color={props.lesson.done || props.lesson.current ? 'editorialAccentDeep' : 'editorialInk3'}
      >
        <Dynamic component={typeIcon(props.lesson.type, props.lesson.done)} size={15} />
      </Flex>

      <Flex align="center" gap="8px" minWidth="0">
        <Box
          fontSize="13px"
          color={props.lesson.done ? 'editorialInk2' : 'editorialInk'}
          fontWeight={props.lesson.current ? '600' : '400'}
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {props.lesson.title}
        </Box>
        <Show when={props.lesson.type === 'quiz'}>
          <Box
            fontSize="9px"
            fontWeight="700"
            letterSpacing=".05em"
            textTransform="uppercase"
            color="editorialInk3"
            border="1px solid var(--colors-editorial-hair-strong)"
            borderRadius="5px"
            padding="1px 5px"
            flex="none"
          >
            Quiz
          </Box>
        </Show>
      </Flex>
    </Grid>
  )

  return (
    <Show when={clickable()} fallback={row}>
      <A href={props.lesson.slug ?? ''}>{row}</A>
    </Show>
  )
}
